import React from 'react';

/**
 * Shared content block for the Privacy Policy. Rendered by PrivacyPage and
 * available for reuse in dialogs/modals if ever needed.
 *
 * Scoped narrowly to what the platform actually does: account data, payment
 * processing via Stripe, push notifications via Firebase Cloud Messaging,
 * location helpers via BigDataCloud/zippopotam, and media uploads via AWS
 * S3. Celey does not use third-party product analytics for V1. Kept
 * plain-text and Apple-review friendly.
 */
const PrivacyContent = () => {
  return (
    <div className="prose prose-sm max-w-none text-gray-700" data-testid="privacy-content">
      <section className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">1. Overview</h2>
        <p>
          This Privacy Policy explains how CeleyApp (&quot;we&quot;, &quot;us&quot;, &quot;the
          Platform&quot;, &quot;Celey&quot;), operated by The KRS Group LLC, collects, uses, and protects your
          information when you use the Celey mobile app, website, and related services. By
          using Celey you agree to the practices described here. If you do not agree,
          please do not use Celey.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">2. Information We Collect</h2>
        <p>We collect the minimum information needed to provide the service:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li><span className="font-medium">Account data</span> — name, email, password (hashed), phone number, profile photo, role (customer or provider).</li>
          <li><span className="font-medium">Provider data</span> — business name, service categories, location (ZIP, city, state), description, photos, pricing, availability.</li>
          <li><span className="font-medium">Event &amp; booking data</span> — event type, date, location, guest count, messages exchanged with providers, agreed prices.</li>
          <li><span className="font-medium">Payment data</span> — handled by Stripe. We do not store full card numbers; we receive a tokenized reference to your payment method via Stripe.</li>
          <li><span className="font-medium">Device data</span> — device push notification token (when you opt in), operating system, app version.</li>
          <li><span className="font-medium">Usage data</span> — Celey does not currently collect behavioural usage analytics or use a third-party analytics SDK.</li>
        </ul>

        <h3 className="text-base font-semibold text-gray-900 mt-4 mb-2">2.1 Data Categories (App Store privacy disclosure)</h3>
        <p>
          The following list mirrors the data-type categories declared in the
          app&apos;s privacy manifest and the matching App Store Connect privacy
          questionnaire. All categories below are <em>linked to your user
          account</em>, are <em>not used to track you across other companies&apos;
          apps or websites</em>, and are collected only for app functionality:
        </p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li><span className="font-medium">Email address</span> — account identity, transactional and notification emails.</li>
          <li><span className="font-medium">Name</span> — first/last name and, for providers, business name shown on profile and proposals.</li>
          <li><span className="font-medium">Phone number</span> — optional; used to coordinate bookings.</li>
          <li><span className="font-medium">Photos or videos</span> — profile photos, portfolio uploads, event photos, and message attachments.</li>
          <li><span className="font-medium">Approximate (coarse) location</span> — used to suggest nearby service providers. We do not request precise GPS coordinates.</li>
          <li><span className="font-medium">User ID</span> — internal account identifier that links your activity within Celey.</li>
          <li><span className="font-medium">Purchase / payment history</span> — booking deposits, final payments, and refunds (metadata only; card numbers stay with Stripe).</li>
          <li><span className="font-medium">Device ID / push token</span> — opt-in Firebase Cloud Messaging token used to deliver booking notifications.</li>
          <li><span className="font-medium">User-generated content (messages)</span> — in-app chat messages between Customers and Service Providers, including any attached images.</li>
        </ul>
        <p className="mt-2 text-xs text-gray-500">
          If additional categories of data are ever collected (for example,
          adding crash reporting or marketing analytics in the future), this
          policy and the App Store privacy questionnaire will be updated before
          the new collection begins.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">3. How We Use Your Information</h2>
        <ul className="list-disc pl-6 space-y-1">
          <li>Operate the event planning platform (matching customers with vendors, exchanging messages, sending proposals).</li>
          <li>Process payments and payouts through Stripe Connect.</li>
          <li>Send push notifications and in-app alerts about inquiries, proposals, bookings, payments, and messages.</li>
          <li>Improve the product based on direct customer feedback and aggregate platform metrics. Celey does not use third-party product-analytics SDKs.</li>
          <li>Detect, prevent, and respond to fraud, abuse, and Terms violations.</li>
          <li>Comply with legal obligations.</li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">4. Third-Party Services</h2>
        <p>We share limited data with the following processors, each under their own privacy terms:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li><span className="font-medium">Stripe</span> — payment and payout processing.</li>
          <li><span className="font-medium">Firebase Cloud Messaging (Google)</span> — push notification delivery.</li>
          <li><span className="font-medium">AWS S3</span> — secure storage of photos you upload.</li>
          <li><span className="font-medium">BigDataCloud / Zippopotam</span> — converting ZIP codes and coordinates to city/state. No persistent identifiers are sent.</li>
        </ul>
        <p className="mt-3">We do not sell your personal information.</p>
      </section>

      <section className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">5. Push Notifications &amp; Permissions</h2>
        <p>
          Push notifications are opt-in. Your device token is stored only after you grant
          permission and is used to send service-related alerts (new inquiries, proposals,
          bookings, payments, and messages). You can revoke the permission at any time in
          iOS Settings or from the in-app Notification Settings screen.
        </p>
        <p className="mt-2">
          Camera and Photo Library access are requested only when you choose to upload a
          photo (profile, portfolio, service image, or message attachment).
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">6. Data Retention</h2>
        <p>
          We retain personal information for as long as your account is active. When you
          delete your account, we remove or anonymize personal identifiers within a
          reasonable period, except where retention is required for legal, accounting, tax,
          fraud-prevention, or dispute-resolution purposes.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">7. Your Rights &amp; Account Deletion</h2>
        <p>You can:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Access, correct, or update your profile and account information at any time from Account Settings.</li>
          <li>Delete your account directly in the app from <span className="font-medium">Account → Danger Zone → Delete Account</span>. Deletion is irreversible.</li>
          <li>Request a copy of your data by contacting us (see Section 10).</li>
          <li>Opt out of non-essential analytics by disabling tracking in the relevant device or browser settings.</li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">8. Security</h2>
        <p>
          We use industry-standard safeguards including TLS for data in transit, hashed
          passwords, encrypted secrets, and access controls. No method of transmission or
          storage is 100% secure; we encourage you to use a strong, unique password.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">9. Children&apos;s Privacy</h2>
        <p>
          The Platform is not directed to children under 13, and we do not knowingly
          collect personal information from children. If you believe a child has provided
          us with information, please contact us so we can delete it.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">10. Contact</h2>
        <p>
          Questions, requests, or concerns? Email{' '}
          <a className="text-emerald-700 underline" href="mailto:celeyapp@gmail.com">
            celeyapp@gmail.com
          </a>
          . We respond within a reasonable time.
        </p>
      </section>

      <section className="mb-2">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">11. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. Material changes will be
          announced in-app or by email before they take effect. Continued use of the
          Platform after the effective date constitutes acceptance.
        </p>
      </section>
    </div>
  );
};

export default PrivacyContent;
